import { KYC_VALIDATOR_STATUS } from '@apptypes/UserStatus.ts';
import LoadingButton from '@components/LoadingButton.tsx';
import { useIsMobile } from '@hooks/use-mobile.tsx';
import { Waiting } from '@v2/components/waiting/waiting.tsx';
import clsx from 'clsx';
import { PlusCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import { isMobile } from 'react-device-detect';
import { useHistory, useLocation } from 'react-router-dom';
import Link from '../../../components/Link';
import { ComponentName } from '../../../constants';
import { ComponentSettings, PagesName } from '../../../constants';
import { usePrevious } from '../../../hooks';
import { useRoutePath } from '../../../hooks';
import { useAuth } from '../../../hooks/useAuth';
import { useConfig } from '../../../hooks/useConfig';
import useDesktopWidth from '../../../hooks/useDesktopWidth';
import useGTM from '../../../hooks/useGTM';
import { useI18n } from '../../../hooks/useI18n';
import useOnClickOutside from '../../../hooks/useOnClickOutside';
import { useUIConfig } from '../../../hooks/useUIConfig';
import { gtmDatakey } from '../../../utils/GoogleTagManager';
import { sortAscending } from '../../../utils/index';
import UserInfoBlock from '../components/header/UserInfoBlock';
import { StyledRowHeader } from '../components/styled/StyledHeader';

interface UserBlockProps {
  mobile: boolean;
  needsBurger?: boolean;
  onlogout?: () => void;
}
const UserBlock = ({ mobile, needsBurger, onlogout }: UserBlockProps) => {
  const { t } = useI18n();
  const { user, signout } = useAuth();
  const { backdrop } = useUIConfig();
  const prevUser = usePrevious(user.logged_in);
  const loginPagePath = useRoutePath(PagesName.LoginPage, true);
  const registerPagePath = useRoutePath(PagesName.RegisterPage, true);

  const isMobile = useIsMobile();

  const responsibleGamingPath = useRoutePath(
    PagesName.ResponsibleGamingPage,
    true,
  );
  useEffect(() => {
    if (user.logged_in && !prevUser) {
      backdrop.hide();
    }
  }, [user]);
  if (user.id) {
    return (
      <div className={'d-flex'}>
        {window.__config__.name === 'rwb' && (
          <Link to={responsibleGamingPath} useRedirect={isMobile}>
            <img
              width={'40px'}
              className="mr-1"
              alt={t('responsible_gaming_link_title')}
              src="/assets/images/footer/responsible-gaming.png"
            />
          </Link>
        )}
        <UserInfoBlock
          dropdownClasses={clsx('d-flex', !mobile && 'mr-1')}
          isMobile={mobile}
          handleLogout={onlogout}
        />
      </div>
    );
  }
  return (
    <div className="login-actions-wrp">
      {window.__config__.name === 'rwb' && (
        <Link to={responsibleGamingPath} useRedirect={isMobile}>
          <img
            width={isMobile ? '32px' : '40px'}
            className="mr-2"
            alt={t('responsible_gaming_link_title')}
            src="/assets/images/footer/responsible-gaming.png"
          />
        </Link>
      )}

      <Button
        as={Link}
        to={loginPagePath}
        variant="secondary"
        className="mr-2"
        useRedirect={isMobile}
      >
        {t('login_btn')}
      </Button>

      <Button
        as={Link}
        to={registerPagePath}
        variant="primary"
        useRedirect={isMobile}
      >
        {t('register_btn')}
      </Button>
    </div>
  );
};

const PageHeader = () => {
  const { t } = useI18n();
  const { user, signout } = useAuth();
  const location = useLocation();
  const { header } = useConfig((prev, next) => !!prev.header === !!next.header);
  const { backdrop, headerNav } = useUIConfig();
  const sendDataToGTM = useGTM();
  const desktopWidth = useDesktopWidth(1199);
  const tabletWidth = useDesktopWidth(991);
  const [navExpanded, setNavExpanded] = useState(false);
  const navbarLinksRef = useRef(null);
  const navbarContainerRef = useRef(null);
  const history = useHistory();
  const [isLoginOut, setIsLoginOut] = useState<boolean>(false);
  const doLogout = async () => {
    setIsLoginOut(true);
    await signout();
    setTimeout(() => {
      setIsLoginOut(false);
      history.replace('/');
    }, 1000);
  };

  useOnClickOutside(
    navbarLinksRef,
    () =>
      headerNav.active?.startsWith('click:') &&
      headerNav.toggle(headerNav.active),
  );
  useOnClickOutside(navbarContainerRef, () => {
    if (!desktopWidth && navExpanded) {
      setTimeout(() => {
        backdrop.toggle(false);
        setNavExpanded(false);
      }, 200);
    }
  });
  const onGtmLinkClick = (name: string) => {
    sendDataToGTM({
      event: 'MainNavigationClick',
      [gtmDatakey('ItemClicked')]: t(name),
    });
  };
  const casinoRoute = useRoutePath(PagesName.CasinoPage);
  const { needsBurger, needsRgLogo = true } = ComponentSettings?.header ?? {};
  const responsibleGamingPath = useRoutePath(
    PagesName.ResponsibleGamingPage,
    true,
  );

  if (
    window.location.href ===
    `${window.location.origin}/account/deposit-response`
  ) {
    return null;
  }

  return (
    <div className={'d-flex align-items-center flex-column'}>
      {isLoginOut && <Waiting />}
      <StyledRowHeader
        className={clsx(
          backdrop.ignoredComponents.includes(ComponentName.Header) && 'on-top',
          'styled-row-header p-0',
        )}
      >
        <div className={'container-fluid  px-4 py-lg-0 py-2'}>
          <div className={'d-flex flex-column flex-grow-1'}>
            <div className="row">
              <div className="col-4 col-md-3 pl-0">
                <div
                  className={
                    'd-flex justify-content-lg-start justify-content-between align-items-center h-100'
                  }
                >
                  <div
                    className={clsx(
                      'd-flex justify-content-start align-items-center',
                    )}
                  >
                    <Link
                      to={casinoRoute}
                      useRedirect={isMobile}
                      className="d-flex flex-column justify-content-center align-items-center"
                      style={{ minHeight: isMobile ? '32px' : '80px' }}
                    >
                      <img
                        alt="logo"
                        className={clsx(
                          desktopWidth
                            ? 'header-logo img-thumbnail position-relative bg-transparent'
                            : 'header-logo-mobile position-relative bg-transparent',
                        )}
                        src={`/assets/images/logo/${desktopWidth ? 'header-logo' : 'logo-small'}.png`}
                        style={{ boxShadow: 'none' }}
                      />
                    </Link>

                    {needsRgLogo && window.__config__.name !== 'rwb' && (
                      <Link
                        to={responsibleGamingPath}
                        useRedirect={isMobile}
                        className={
                          'd-flex flex-column justify-content-center align-items-center h-100 pl-sm-2'
                        }
                      >
                        <img
                          className="responsible-gaming"
                          alt={t('responsible_gaming_link_title')}
                          src="/assets/images/footer/responsible-gaming.png"
                        />
                      </Link>
                    )}
                  </div>
                </div>
              </div>

              <div className="d-none d-md-block col-md-6">
                <div
                  className={
                    'd-flex flex-column justify-content-center align-items-center h-100'
                  }
                >
                  {!(needsBurger && !tabletWidth) && (
                    <div className="nav-links d-flex align-items-center justify-content-center h-100">
                      {header
                        ?.concat()
                        .sort((a, b) =>
                          sortAscending(a.order || 0, b.order || 0),
                        )
                        .map(link => {
                          if (!link.path) return null;
                          return (
                            <span
                              key={link.path}
                              className={clsx(
                                'nav-links__link d-flex align-items-center justify-content-center text-center',
                                location.pathname === link.path && 'active',
                              )}
                            >
                              <Link
                                className="d-flex align-items-center justify-content-center"
                                to={link.path}
                                onClick={() => onGtmLinkClick(link.name)}
                              >
                                {link.icon && <i className={link.icon} />}
                                {t(link.name)}
                              </Link>
                            </span>
                          );
                        })}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-8 col-md-3 pr-0">
                <div
                  className={
                    'd-flex justify-content-end align-items-center h-100'
                  }
                >
                  <div className="user-block">
                    <UserBlock
                      mobile={true}
                      needsBurger={needsBurger}
                      onlogout={doLogout}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </StyledRowHeader>
    </div>
  );
};

export default PageHeader;
